import React, { useState } from "react";
import { Row, Col, Nav, NavItem, NavLink, TabContent, TabPane, Modal, ModalHeader, ModalBody } from "reactstrap";
import classnames from "classnames";
import { 
    NeoInput, 
    NeoAlert, 
    NeoButton, 
    NeoCard, 
    NeoBadge, 
    Breadcrumb,
    NeoSideMenu
} from "../../../components/Common";
import usePlatformSettings from "./hooks/usePlatformSettings";
import { GATEWAY_OPTIONS } from "./constants";
import neoLogo from "../../../assets/images/Neo_Logo_White.svg";

const AUTOMATION_TRIGGERS_LIST = [
    {
        id: "lead_created",
        name: "Novo Lead",
        description: "Disparado quando um rascunho de tenant é criado via checkout público.",
        icon: "mdi-account-plus-outline",
        color: "primary",
        delayLabel: "Atraso para Disparo (Dias)",
        delayDescription: "Enviar X dias após o cadastro do lead (0 = imediatamente).",
    },
    {
        id: "client_created",
        name: "Novo Cliente",
        description: "Disparado quando o tenant inicia o período de testes ou ativa a assinatura.",
        icon: "mdi-account-check-outline",
        color: "success",
        delayLabel: "Atraso para Disparo (Dias)",
        delayDescription: "Enviar X dias após a ativação do cliente (0 = imediatamente).",
    },
    {
        id: "billing_reminder",
        name: "Mensalidade / Boleto do Mês",
        description: "Disparado X dias antes do vencimento da mensalidade do tenant.",
        icon: "mdi-cash-clock",
        color: "info",
        delayLabel: "Antecedência de Envio (Dias antes do vencimento)",
        delayDescription: "Enviar X dias ANTES do vencimento. Ex: 5 = enviado 5 dias antes da cobrança.",
    },
    {
        id: "payment_overdue",
        name: "Pagamento Atrasado",
        description: "Disparado quando uma fatura mensal do tenant fica em atraso.",
        icon: "mdi-alert-octagon-outline",
        color: "danger",
        delayLabel: "Dias de Tolerância (apenas informativo no e-mail)",
        delayDescription: "Valor exibido no e-mail como referência. O disparo ocorre imediatamente quando a fatura entra em atraso, independente deste número.",
    },
    {
        id: "client_inactive",
        name: "Cliente Inativo (Sem Uso)",
        description: "Disparado quando o tenant passa X dias sem registrar qualquer atividade.",
        icon: "mdi-account-clock-outline",
        color: "warning",
        delayLabel: "Dias Sem Uso para Disparar",
        delayDescription: "Enviar quando o cliente ficar X dias sem acessar o sistema.",
    }
];

const TEMPLATE_VARIABLES = [
    { key: "[nome_cliente]", label: "Nome do Cliente", example: "João da Silva" },
    { key: "[nome_academia]", label: "Nome da Academia", example: "NEO Fit" },
    { key: "[trial_days]", label: "Dias Configurados (trial/antecedência/atraso)", example: "15" },
    { key: "[valor]", label: "Valor Comercial", example: "R$ 490,00" }
];

const PlatformSettings = () => {
    const { 
        config, 
        loading, 
        saving, 
        error, 
        successMessage, 
        evolutionStatus,
        qrCode,
        loadingQr,
        updateField, 
        updateNestedField,
        handleSave,
        generateQRCode,
        disconnectWhatsApp
    } = usePlatformSettings();

    const [activeMainTab, setActiveMainTab] = useState("1");
    const [selectedAutomation, setSelectedAutomation] = useState("lead_created");
    const [previewModalOpen, setPreviewModalOpen] = useState(false);

    const toggleMainTab = (tab) => {
        if (activeMainTab !== tab) setActiveMainTab(tab);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
            </div>
        );
    }

    const currentAuto = config.automations?.[selectedAutomation] || {
        active: false,
        channels: { email: false, whatsapp: false, call: false },
        emailSubject: "",
        emailBody: "",
        whatsappMessage: "",
        callScript: "",
        delayDays: 0
    };

    const handleInsertVariable = (inputFieldId, variable) => {
        const textarea = document.getElementById(inputFieldId);
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentText = textarea.value || "";
        const updatedText = currentText.substring(0, start) + variable + currentText.substring(end);
        
        let path = "";
        if (inputFieldId === "emailSubject") path = `automations.${selectedAutomation}.emailSubject`;
        else if (inputFieldId === "emailBody") path = `automations.${selectedAutomation}.emailBody`;
        else if (inputFieldId === "whatsappMessage") path = `automations.${selectedAutomation}.whatsappMessage`;
        else if (inputFieldId === "callScript") path = `automations.${selectedAutomation}.callScript`;

        updateNestedField(path, updatedText);

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + variable.length, start + variable.length);
        }, 50);
    };

    // Helper para status visual da Evolution
    const getEvolutionStatusBadge = () => {
        switch (evolutionStatus) {
            case "open":
                return <NeoBadge color="success"><i className="mdi mdi-check-circle me-1"></i>Conectado (Online)</NeoBadge>;
            case "connecting":
                return <NeoBadge color="warning"><i className="mdi mdi-spin mdi-loading me-1"></i>Aguardando Conexão</NeoBadge>;
            case "close":
                return <NeoBadge color="danger"><i className="mdi mdi-close-circle me-1"></i>Desconectado (Offline)</NeoBadge>;
            case "error":
                return <NeoBadge color="danger"><i className="mdi mdi-alert me-1"></i>Erro de Conexão</NeoBadge>;
            case "not_created":
                return <NeoBadge color="info"><i className="mdi mdi-plus-box me-1"></i>Instância não Iniciada</NeoBadge>;
            case "not_configured":
                return <NeoBadge color="muted"><i className="mdi mdi-cog-off me-1"></i>Não Configurado</NeoBadge>;
            default:
                return <NeoBadge color="muted"><i className="mdi mdi-spin mdi-loading me-1"></i>Verificando...</NeoBadge>;
        }
    };

    return (
        <div className="pb-5">
            <div className="mb-4">
                <Breadcrumb title="Configurações do SaaS" />
            </div>

            {successMessage && (
                <NeoAlert color="success" className="mb-4 shadow-sm border-0" animate={false}>
                    {successMessage}
                </NeoAlert>
            )}
            {error && (
                <NeoAlert color="danger" className="mb-4 shadow-sm border-0" animate={false}>
                    {error}
                </NeoAlert>
            )}

            {/* Menu de Abas Global */}
            <Nav tabs className="status-tabs-nav mb-4 border-bottom">
                <NavItem>
                    <NavLink
                        className={classnames("status-tab-link border-0 cursor-pointer", { active: activeMainTab === "1" })}
                        onClick={() => toggleMainTab("1")}
                    >
                        <i className="mdi mdi-cog-outline me-2"></i>
                        Geral
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={classnames("status-tab-link border-0 cursor-pointer", { active: activeMainTab === "2" })}
                        onClick={() => toggleMainTab("2")}
                    >
                        <i className="mdi mdi-send-circle-outline me-2"></i>
                        Automação de Envios
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={classnames("status-tab-link border-0 cursor-pointer", { active: activeMainTab === "3" })}
                        onClick={() => toggleMainTab("3")}
                    >
                        <i className="mdi mdi-whatsapp me-2"></i>
                        Integração WhatsApp
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={classnames("status-tab-link border-0 cursor-pointer", { active: activeMainTab === "4" })}
                        onClick={() => toggleMainTab("4")}
                    >
                        <i className="mdi mdi-email-outline me-2"></i>
                        Servidor de E-mail (SMTP)
                    </NavLink>
                </NavItem>
            </Nav>

            <TabContent activeTab={activeMainTab}>
                {/* 1. ABA GERAL */}
                <TabPane tabId="1">
                    <Row className="g-4">
                        <Col lg={7}>
                            <NeoCard title="Identidade & Contato" headerIcon="mdi-account-card-details-outline" thinBorder bodyClassName="p-4" className="h-100">
                                <Row className="g-3">
                                    <Col md={6}>
                                        <NeoInput label="Nome Comercial do SaaS *" value={config.saasName || ""} onChange={(e) => updateField("saasName", e.target.value)} />
                                    </Col>
                                    <Col md={6}>
                                        <NeoInput label="Subdomínio do Painel *" value={config.subdomain || ""} onChange={(e) => updateField("subdomain", e.target.value)} />
                                    </Col>
                                    <Col md={6}>
                                        <NeoInput label="E-mail de Suporte *" type="email" value={config.supportEmail || ""} onChange={(e) => updateField("supportEmail", e.target.value)} />
                                    </Col>
                                    <Col md={6}>
                                        <NeoInput label="WhatsApp de Suporte *" value={config.supportPhone || ""} onChange={(e) => updateField("supportPhone", e.target.value)} />
                                    </Col>
                                    <Col md={6}>
                                        <NeoInput label="Link dos Termos de Uso" type="url" value={config.termsUrl || ""} onChange={(e) => updateField("termsUrl", e.target.value)} />
                                    </Col>
                                    <Col md={6}>
                                        <NeoInput label="Link da Política de Privacidade" type="url" value={config.privacyUrl || ""} onChange={(e) => updateField("privacyUrl", e.target.value)} />
                                    </Col>
                                </Row>
                            </NeoCard>
                        </Col>

                        <Col lg={5}>
                            <NeoCard title="Gatilhos & Gateway" headerIcon="mdi-credit-card-settings-outline" thinBorder bodyClassName="p-4 d-flex flex-column justify-content-between" className="h-100">
                                <div>
                                    <div className="platform-form-group mb-3">
                                        <NeoInput
                                            label="Gateway de Faturamento *"
                                            type="select"
                                            value={config.gateway || "Mercado Pago"}
                                            onChange={(e) => updateField("gateway", e.target.value)}
                                            options={GATEWAY_OPTIONS}
                                        />
                                    </div>
                                    <div className="platform-form-group mb-3">
                                        <NeoInput label="Período Padrão de Teste (Dias) *" type="number" value={config.trialDays || 0} onChange={(e) => updateField("trialDays", Number(e.target.value))} />
                                    </div>
                                    <div className="platform-form-group">
                                        <NeoInput label="Tolerância para Bloqueio Automático (Dias) *" type="number" value={config.inadimplencyBlockDays || 0} onChange={(e) => updateField("inadimplencyBlockDays", Number(e.target.value))} />
                                        <span className="text-xs text-muted mt-1 d-block">Dias após o vencimento da fatura para suspensão automática do acesso.</span>
                                    </div>
                                </div>
                            </NeoCard>
                        </Col>
                    </Row>
                </TabPane>

                {/* 2. ABA AUTOMAÇÕES */}
                <TabPane tabId="2">
                    <Row>
                        {/* Lista de Eventos (Master) */}
                        <Col lg={4}>
                            <NeoSideMenu
                                title="Gatilhos Disponíveis"
                                subtitle="Automação de disparos"
                                icon="mdi-format-list-bulleted"
                                activeId={selectedAutomation}
                                onSelect={(id) => setSelectedAutomation(id)}
                                items={AUTOMATION_TRIGGERS_LIST.map((t) => {
                                    const triggerData = config.automations?.[t.id] || {};
                                    return {
                                        id: t.id,
                                        label: t.name,
                                        description: t.description,
                                        icon: t.icon,
                                        badge: triggerData.active ? "Ativo" : "Pausado",
                                        badgeVariant: triggerData.active ? "success" : "muted"
                                    };
                                })}
                            />
                        </Col>

                        {/* Configurações do Evento (Detail) */}
                        <Col lg={8}>
                            {selectedAutomation && (
                                <NeoCard
                                    title={`Configurar Gatilho: ${AUTOMATION_TRIGGERS_LIST.find(a => a.id === selectedAutomation)?.name}`}
                                    headerIcon="mdi-tune"
                                    headerActions={
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="font-size-12 fw-bold text-muted">
                                                {currentAuto.active ? "Ativo" : "Inativo"}
                                            </span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input cursor-pointer"
                                                    checked={currentAuto.active || false}
                                                    onChange={(e) => updateNestedField(`automations.${selectedAutomation}.active`, e.target.checked)}
                                                    style={{ width: "40px", height: "20px" }}
                                                />
                                            </div>
                                        </div>
                                    }
                                    thinBorder
                                    bodyClassName="p-4"
                                >
                                    <div className="mb-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <NeoInput
                                                    label={AUTOMATION_TRIGGERS_LIST.find(t => t.id === selectedAutomation)?.delayLabel || "Atraso para Disparo (Dias)"}
                                                    type="number"
                                                    min="0"
                                                    value={currentAuto.delayDays || 0}
                                                    onChange={(e) => updateNestedField(`automations.${selectedAutomation}.delayDays`, Number(e.target.value))}
                                                />
                                                <span className="text-xs text-muted d-block mt-1">
                                                    {AUTOMATION_TRIGGERS_LIST.find(t => t.id === selectedAutomation)?.delayDescription || "Disparar X dias após o evento acontecer (0 = imediatamente)."}
                                                </span>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Escolha de Canais */}
                                    <h6 className="fw-bold text-dark font-size-13 mb-3 border-bottom pb-2">Canais de Envio Ativos</h6>
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <div className="d-flex align-items-center gap-2 p-2 border rounded bg-light">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input cursor-pointer ms-0 me-1"
                                                    id="channel-email"
                                                    checked={currentAuto.channels?.email || false}
                                                    onChange={(e) => updateNestedField(`automations.${selectedAutomation}.channels.email`, e.target.checked)}
                                                />
                                                <label htmlFor="channel-email" className="mb-0 fw-semibold text-dark cursor-pointer font-size-13"><i className="mdi mdi-email-outline me-1"></i>E-mail</label>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="d-flex align-items-center gap-2 p-2 border rounded bg-light">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input cursor-pointer ms-0 me-1"
                                                    id="channel-whats"
                                                    checked={currentAuto.channels?.whatsapp || false}
                                                    onChange={(e) => updateNestedField(`automations.${selectedAutomation}.channels.whatsapp`, e.target.checked)}
                                                />
                                                <label htmlFor="channel-whats" className="mb-0 fw-semibold text-dark cursor-pointer font-size-13"><i className="mdi mdi-whatsapp me-1"></i>WhatsApp</label>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="d-flex align-items-center gap-2 p-2 border rounded bg-light">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input cursor-pointer ms-0 me-1"
                                                    id="channel-call"
                                                    checked={currentAuto.channels?.call || false}
                                                    onChange={(e) => updateNestedField(`automations.${selectedAutomation}.channels.call`, e.target.checked)}
                                                />
                                                <label htmlFor="channel-call" className="mb-0 fw-semibold text-dark cursor-pointer font-size-13"><i className="mdi mdi-phone-outline me-1"></i>Ligação (Script)</label>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Área de Variáveis Dinâmicas */}
                                    <div className="bg-light p-3 rounded mb-4">
                                        <small className="text-muted fw-bold d-block mb-2 text-uppercase font-size-10 letter-spacing-1">
                                            Variáveis Disponíveis (Clique para inserir):
                                        </small>
                                        <div className="d-flex gap-2 flex-wrap">
                                            {TEMPLATE_VARIABLES.map(v => (
                                                <span
                                                    key={v.key}
                                                    className="neo-variable-badge cursor-pointer px-2 py-1 bg-white border rounded text-xs font-size-11 d-inline-flex align-items-center gap-1 shadow-sm hover-brightness"
                                                    onClick={() => {
                                                        // Tenta inserir na área ativa
                                                        if (currentAuto.channels?.whatsapp) {
                                                            handleInsertVariable("whatsappMessage", v.key);
                                                        } else if (currentAuto.channels?.email) {
                                                            handleInsertVariable("emailBody", v.key);
                                                        }
                                                    }}
                                                    title={`Clique para inserir: ${v.key} (${v.label})`}
                                                >
                                                    <i className="mdi mdi-plus-circle text-primary"></i>
                                                    {v.label}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Editor E-mail */}
                                    {currentAuto.channels?.email && (
                                        <div className="border rounded p-3 mb-3 bg-white shadow-sm">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="fw-bold font-size-12 text-primary text-uppercase m-0"><i className="mdi mdi-email-edit-outline me-1"></i>Modelo de E-mail</h6>
                                                <NeoButton
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => setPreviewModalOpen(true)}
                                                    icon="mdi-eye-outline"
                                                    className="py-1 px-2.5 font-size-11"
                                                >
                                                    Visualizar E-mail
                                                </NeoButton>
                                            </div>
                                            <div className="mb-3">
                                                <NeoInput
                                                    id="emailSubject"
                                                    label="Assunto do E-mail"
                                                    value={currentAuto.emailSubject || ""}
                                                    onChange={(e) => updateNestedField(`automations.${selectedAutomation}.emailSubject`, e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <NeoInput
                                                    id="emailBody"
                                                    type="textarea"
                                                    label="Conteúdo do E-mail"
                                                    rows="5"
                                                    value={currentAuto.emailBody || ""}
                                                    onChange={(e) => updateNestedField(`automations.${selectedAutomation}.emailBody`, e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Editor WhatsApp */}
                                    {currentAuto.channels?.whatsapp && (
                                        <div className="border rounded p-3 mb-3 bg-white shadow-sm">
                                            <h6 className="fw-bold font-size-12 text-success text-uppercase mb-3"><i className="mdi mdi-whatsapp me-1"></i>Mensagem de WhatsApp</h6>
                                            <NeoInput
                                                id="whatsappMessage"
                                                type="textarea"
                                                label="Mensagem de Texto"
                                                rows="4"
                                                value={currentAuto.whatsappMessage || ""}
                                                onChange={(e) => updateNestedField(`automations.${selectedAutomation}.whatsappMessage`, e.target.value)}
                                            />
                                        </div>
                                    )}

                                    {/* Editor Ligação */}
                                    {currentAuto.channels?.call && (
                                        <div className="border rounded p-3 bg-white shadow-sm">
                                            <h6 className="fw-bold font-size-12 text-warning text-uppercase mb-3"><i className="mdi mdi-phone me-1"></i>Roteiro / Instruções de Ligação</h6>
                                            <NeoInput
                                                id="callScript"
                                                type="textarea"
                                                label="Script Outbound (para ligar manualmente ou via robô)"
                                                rows="3"
                                                value={currentAuto.callScript || ""}
                                                onChange={(e) => updateNestedField(`automations.${selectedAutomation}.callScript`, e.target.value)}
                                            />
                                            <span className="text-xs text-muted d-block mt-1">Isso gerará uma tarefa de suporte/ligação automática na fila do admin.</span>
                                        </div>
                                    )}
                                </NeoCard>
                            )}
                        </Col>
                    </Row>
                </TabPane>

                {/* 3. ABA INTEGRACAO WHATSAPP */}
                <TabPane tabId="3">
                    <Row className="g-4">
                        <Col lg={7}>
                            <NeoCard title="Credenciais da Evolution API" headerIcon="mdi-server-network" thinBorder bodyClassName="p-4" className="h-100">
                                <p className="text-muted small mb-4">Insira as credenciais do servidor Evolution dedicado à plataforma. Estas configurações servem para os disparos automáticos do SaaS aos inquilinos/tenants.</p>
                                <div className="mb-3">
                                    <NeoInput
                                        label="URL da API (Evolution)"
                                        value={config.evolutionApiUrl || ""}
                                        onChange={(e) => updateField("evolutionApiUrl", e.target.value)}
                                        placeholder="https://api-whats.neoapp.com"
                                    />
                                </div>
                                <div className="mb-3">
                                    <NeoInput
                                        label="Chave Global API Key (Global Token)"
                                        type="password"
                                        value={config.evolutionApiKey || ""}
                                        onChange={(e) => updateField("evolutionApiKey", e.target.value)}
                                        placeholder="Seu token global..."
                                    />
                                </div>
                                <div className="mb-3">
                                    <NeoInput
                                        label="Nome da Instância do SaaS"
                                        value={config.evolutionInstanceName || ""}
                                        onChange={(e) => updateField("evolutionInstanceName", e.target.value)}
                                        placeholder="neoapp-saas-admin"
                                    />
                                </div>
                            </NeoCard>
                        </Col>

                        <Col lg={5}>
                            <NeoCard title="Status do WhatsApp" headerIcon="mdi-cellphone-link" headerActions={getEvolutionStatusBadge()} thinBorder bodyClassName="p-4 d-flex flex-column justify-content-between h-100">
                                <div>
                                    <p className="text-muted small mb-4">Verifique a conexão ou emparelhe um novo aparelho para disparar mensagens do SaaS.</p>
                                    
                                    {/* Se estiver conectado */}
                                    {evolutionStatus === "open" ? (
                                        <div className="text-center py-4 bg-success bg-opacity-5 rounded border border-success border-opacity-10 mb-3">
                                            <i className="mdi mdi-check-decagram text-success font-size-48 mb-2 d-block"></i>
                                            <h6 className="fw-bold text-success">Instância Ativa</h6>
                                            <p className="text-xs text-muted mb-0">Seu telefone está conectado e pronto para automações.</p>
                                        </div>
                                    ) : qrCode ? (
                                        // Exibe o QR Code se estiver carregado
                                        <div className="text-center py-3">
                                            <p className="text-xs fw-semibold text-danger mb-3">Escaneie o QR Code abaixo no WhatsApp:</p>
                                            <div className="bg-white p-2 d-inline-block border rounded shadow-sm">
                                                <img 
                                                    src={qrCode} 
                                                    alt="Evolution API Connection QR Code" 
                                                    style={{ width: "200px", height: "200px" }}
                                                />
                                            </div>
                                            <span className="text-xs text-muted mt-3 d-block"><i className="mdi mdi-loading mdi-spin me-1"></i>Aguardando leitura...</span>
                                        </div>
                                    ) : (
                                        <div className="text-center py-4 bg-light rounded border mb-3">
                                            <i className="mdi mdi-qrcode-scan text-muted font-size-48 mb-2 d-block"></i>
                                            <h6 className="fw-bold text-dark mb-1">Aparelho Desconectado</h6>
                                            <p className="text-xs text-muted mb-0">Escaneie o QR Code para conectar seu número de envio.</p>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 pt-3 border-top d-flex gap-2">
                                    {evolutionStatus === "open" ? (
                                        <NeoButton
                                            variant="danger"
                                            className="w-100 py-2.5"
                                            onClick={disconnectWhatsApp}
                                            loading={loadingQr}
                                            icon="mdi-logout"
                                        >
                                            Desconectar WhatsApp
                                        </NeoButton>
                                    ) : (
                                        <NeoButton
                                            variant="primary"
                                            className="w-100 py-2.5"
                                            onClick={generateQRCode}
                                            loading={loadingQr}
                                            disabled={!config.evolutionApiUrl || !config.evolutionApiKey}
                                            icon="mdi-qrcode"
                                        >
                                            Conectar WhatsApp (QR)
                                        </NeoButton>
                                    )}
                                </div>
                            </NeoCard>
                        </Col>
                    </Row>
                </TabPane>

                {/* 4. ABA SMTP */}
                <TabPane tabId="4">
                    <Row className="g-4">
                        <Col lg={7}>
                            <NeoCard title="Configuração do Servidor SMTP" headerIcon="mdi-email-server" thinBorder bodyClassName="p-4" className="h-100">
                                <p className="text-muted small mb-4">Insira os dados do servidor SMTP corporativo que o NeoApp utilizará para enviar faturas, boletos anexos e mensagens de onboarding aos clientes.</p>
                                <Row className="g-3">
                                    <Col md={8}>
                                        <NeoInput
                                            label="Servidor SMTP (Host) *"
                                            value={config.smtpHost || ""}
                                            onChange={(e) => updateField("smtpHost", e.target.value)}
                                            placeholder="smtp.exemplo.com"
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <NeoInput
                                            label="Porta SMTP *"
                                            type="number"
                                            value={config.smtpPort || 465}
                                            onChange={(e) => updateField("smtpPort", Number(e.target.value))}
                                            placeholder="465"
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <NeoInput
                                            label="Usuário SMTP (E-mail de envio) *"
                                            type="email"
                                            value={config.smtpUser || ""}
                                            onChange={(e) => updateField("smtpUser", e.target.value)}
                                            placeholder="financeiro@gestaoneo.com"
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <NeoInput
                                            label="Senha SMTP *"
                                            type="password"
                                            value={config.smtpPass || ""}
                                            onChange={(e) => updateField("smtpPass", e.target.value)}
                                            placeholder="Sua senha de e-mail..."
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <NeoInput
                                            label="Nome do Remetente"
                                            value={config.smtpSenderName || ""}
                                            onChange={(e) => updateField("smtpSenderName", e.target.value)}
                                            placeholder="Financeiro NeoApp"
                                        />
                                    </Col>
                                </Row>
                            </NeoCard>
                        </Col>

                        <Col lg={5}>
                            <NeoCard title="Instruções de SMTP" headerIcon="mdi-information-outline" thinBorder bodyClassName="p-4" className="h-100">
                                <div className="text-muted small">
                                    <h6 className="text-dark fw-bold mb-2">Recomendações importantes:</h6>
                                    <ul className="ps-3 mb-3 d-flex flex-column gap-2">
                                        <li>Para envio seguro utilizando a porta <strong>465</strong>, garanta que seu servidor suporta conexões <strong>SSL/TLS</strong>.</li>
                                        <li>Se utilizar e-mails do Gmail, Hostinger ou Zoho, lembre-se de gerar uma <strong>Senha de Aplicativo (App Password)</strong> dedicada nas configurações da sua conta de e-mail, em vez de usar sua senha principal de login.</li>
                                        <li>Os e-mails de cobrança anexarão automaticamente as faturas em PDF aos e-mails enviados aos clientes.</li>
                                    </ul>
                                    <div className="alert alert-info border-0 mb-0 d-flex gap-2">
                                        <i className="mdi mdi-alert-circle font-size-18 text-info"></i>
                                        <span>Garante que o remitente preenchido possui autorização SPF/DKIM no seu domínio de e-mail para evitar que as faturas caiam na caixa de SPAM do cliente.</span>
                                    </div>
                                </div>
                            </NeoCard>
                        </Col>
                    </Row>
                </TabPane>
            </TabContent>

            {/* Rodapé fixo para salvar configurações */}
            <div className="d-flex justify-content-end mt-4 pt-4 border-top">
                <NeoButton
                    variant="primary"
                    className="px-5 py-2.5 fw-bold shadow-sm"
                    onClick={handleSave}
                    loading={saving}
                    loadingText="Salvando..."
                    icon="mdi-content-save"
                >
                    Salvar Todas as Configurações
                </NeoButton>
            </div>

            {/* Modal de Visualização do E-mail */}
            <Modal isOpen={previewModalOpen} toggle={() => setPreviewModalOpen(false)} centered size="lg">
                <ModalHeader toggle={() => setPreviewModalOpen(false)} className="neo-font-outfit fw-bold text-dark">
                    <i className="mdi mdi-eye-outline me-2 text-primary"></i>
                    Visualização do E-mail
                </ModalHeader>
                <ModalBody className="p-4" style={{ backgroundColor: "#f7f5f2" }}>
                    <div style={{
                        maxWidth: "600px",
                        margin: "0 auto",
                        backgroundColor: "#ffffff",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 8px 24px rgba(44, 24, 16, 0.06)",
                        fontFamily: "'Outfit', Arial, sans-serif",
                        border: "1px solid #e8e2dc"
                    }}>
                        {/* Header */}
                        <div style={{ backgroundColor: "#2c1810", padding: "32px 24px", textAlign: "center", borderBottom: "4px solid #784b22" }}>
                            <img src={neoLogo} alt="NeoApp Logo" style={{ height: "42px", display: "inline-block" }} />
                        </div>
                        
                        {/* Body */}
                        <div style={{ padding: "40px 32px", color: "#2c1e17", backgroundColor: "#ffffff" }}>
                            <p style={{ fontSize: "16px", fontWeight: "bold", color: "#2c1810", marginTop: 0, marginBottom: "20px" }}>
                                Olá, João da Silva (Academia NEO Fit)
                            </p>
                            
                            <div 
                                style={{ fontSize: "15px", lineHeight: "1.7", color: "#2c1e17" }}
                                dangerouslySetInnerHTML={{
                                    __html: (currentAuto.emailBody || "")
                                        .replace(/\[nome_cliente\]/g, "João da Silva")
                                        .replace(/\[nome_academia\]/g, "NEO Fit")
                                        .replace(/\[trial_days\]/g, config.trialDays || "15")
                                        .replace(/\[valor\]/g, "R$ 490,00")
                                        .replace(/\n/g, "<br/>")
                                }}
                            />
                            
                            {/* Se for cobrança ou lembrete de faturamento, exibe o botão mock de boleto */}
                            {(selectedAutomation === "payment_overdue" || selectedAutomation === "billing_reminder") && (
                                <>
                                    <div style={{ backgroundColor: "#faf8f5", border: "1px dashed #c2b8b0", padding: "16px", borderRadius: "8px", fontFamily: "monospace", wordBreak: "break-all", margin: "24px 0", fontSize: "13px", color: "#2c1810", fontWeight: "bold", textAlign: "center" }}>
                                        34191.79001 01043.513184 91020.150008 7 90000000049000
                                    </div>
                                    <div style={{ margin: "24px 0", textAlign: "center" }}>
                                        <span style={{ backgroundColor: "#784b22", color: "#ffffff", padding: "14px 28px", textDecoration: "none", borderRadius: "8px", fontWeight: "bold", display: "inline-block", fontSize: "15px", boxShadow: "0 4px 12px rgba(120, 75, 34, 0.2)" }}>
                                            Visualizar Boleto PDF
                                        </span>
                                    </div>
                                    <p style={{ color: "#7a6555", fontSize: "13px", lineHeight: "1.5", marginTop: "16px", fontStyle: "italic" }}>
                                        * O acesso continuará liberado. Se o boleto não for pago em até 3 dias, a assinatura será suspensa.
                                    </p>
                                </>
                            )}
                            
                            {/* Central de Ajuda */}
                            <div style={{ marginTop: "32px", backgroundColor: "#faf8f5", borderRadius: "8px", padding: "20px", borderLeft: "4px solid #784b22" }}>
                                <p style={{ margin: "0 0 10px 0", fontWeight: "bold", color: "#2c1810", fontSize: "14px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Precisa de Ajuda?</p>
                                <p style={{ margin: "0 0 6px 0", fontSize: "13px", color: "#5c4e46" }}>
                                    <strong>Suporte por E-mail:</strong> <span style={{ color: "#784b22" }}>{(!config.supportEmail || config.supportEmail === "suporte@neoapp.com.br") ? "contato@gestaoneo.com" : config.supportEmail}</span>
                                </p>
                                <p style={{ margin: 0, fontSize: "13px", color: "#5c4e46" }}>
                                    <strong>Suporte por WhatsApp:</strong> <span style={{ color: "#784b22" }}>{(!config.supportPhone || config.supportPhone === "(79) 99888-7766") ? "(79) 99668-0315" : config.supportPhone}</span>
                                </p>
                            </div>
                            
                            {/* Assinatura */}
                            <div style={{ marginTop: "36px", borderTop: "1px solid #e8e2dc", paddingTop: "24px", fontSize: "14px", color: "#7a6555" }}>
                                <p style={{ margin: 0 }}>Atenciosamente,</p>
                                <p style={{ margin: "4px 0 0 0", fontWeight: "bold", color: "#2c1810" }}>{config.smtpSenderName || "Financeiro NeoApp"}</p>
                            </div>
                        </div>
                        
                        {/* Footer */}
                        <div style={{ backgroundColor: "#2c1810", padding: "28px 24px", textAlign: "center", fontSize: "12px", color: "#a08b7a", borderTop: "1px solid #1e110b" }}>
                            <div style={{ marginBottom: "12px" }}>
                                <span style={{ color: "#c29770", cursor: "pointer", margin: "0 8px" }}>Termos de Uso</span>
                                <span style={{ color: "#4a3328" }}>•</span>
                                <span style={{ color: "#c29770", cursor: "pointer", margin: "0 8px" }}>Privacidade</span>
                            </div>
                            <p style={{ margin: 0, fontWeight: "bold" }}>NeoApp Tecnologia Ltda</p>
                            <p style={{ margin: "4px 0 0 0", fontSize: "11px", opacity: 0.8 }}>Este é um e-mail automático enviado pela plataforma NeoApp. Por favor, não responda diretamente a esta mensagem.</p>
                            <p style={{ margin: "12px 0 0 0", fontSize: "11px", color: "#7a6555" }}>© 2026 NeoApp. Todos os direitos reservados.</p>
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        </div>
    );
};

export default PlatformSettings;
